import { IModuleStateProps, withModuleState } from '@msdyn365-commerce-modules/checkout-utilities';
import { GetCheckoutCartInput } from '@msdyn365-commerce-modules/retail-actions';
import { CommerceProperty } from '@msdyn365-commerce/retail-proxy';
import { updateAsync } from '@msdyn365-commerce/retail-proxy/dist/DataActions/CartsDataActions.g';
import { computed, when } from 'mobx';
import { observer } from 'mobx-react';
import moment from 'moment';
import * as React from 'react';
import { ICheckoutDateOfBirthData } from './checkout-date-of-birth.data';
import { ICheckoutDateOfBirthProps } from './checkout-date-of-birth.props.autogenerated';

interface ICheckoutDateOfBirthState {
    date: string | null;
    submitted: boolean;
}

interface IProps extends ICheckoutDateOfBirthProps<ICheckoutDateOfBirthData>, IModuleStateProps { }

const MIN_AGE = 662695446000; // 21 years in ms
const MAX_BIRTHDAY = -30610195622000; // 1000-01-01 in ms

/**
 * Checkout Terms and Conditions
 */
// @ts-ignore
@withModuleState
@observer
class CheckoutDateOfBirth extends React.Component<IProps, ICheckoutDateOfBirthState> {
    @computed get isDataReady(): boolean {
        return (
            (this.props.data.checkoutState && this.props.data.checkoutState.result && this.props.data.checkoutState.status) === 'SUCCESS'
        );
    }

    public state: ICheckoutDateOfBirthState = {
        date: '',
        submitted: false
    };

    private inputRef: React.RefObject<HTMLInputElement>;

    constructor(props: IProps) {
        super(props);
        // debounded function seems to lose input so we'll use a ref
        this.inputRef = React.createRef();
        console.log('starting...');
    }

    public componentDidMount(): void {
        // @ts-ignore: Compiler not reconizing condition check for function params
        when(
            () => this.isDataReady,
            () => {
                this.init();
            }
        );
    }

    public render(): JSX.Element | null {
        const hasError = this.props.moduleState.hasError;

        return (
            <div className='checkout-date-of-birth' aria-label='date of birth'>
                <label>
                    Date of Birth
                {
                        hasError && (
                            <div
                                id='checkout-date-of-birth'
                                className='checkout-date-of-birth__input-error'
                                role='alert'
                                aria-live='assertive'
                            >
                                {
                                    (Date.parse(this.state.date || '') > MAX_BIRTHDAY) ?
                                    'Must be at least 21 years old.'
                                    : 'Please enter a valid date'
                                }
                            </div>
                        )
                    }
                </label>
                <input
                    ref={this.inputRef}
                    value={this.state.date || ''}
                    disabled={this.state.submitted}
                    aria-label='Your date of birth'
                    aria-labelledby='checkout-date-of-birth'
                    id='checkout-date-of-birth'
                    type='date'
                    placeholder='MM/DD/YYYY'
                    onChange={this.onChange}
                />
            </div>
        );
    }

    private init = (): void => {
        this.props.moduleState.init({
            onEdit: this.onEdit,
            onCancel: this.onCancel,
            onSubmit: this.onSubmit,
            status: 'updating'
        });
    };

    private onEdit = (): void => {
        this.setState({ submitted: false });
        this.props.moduleState.onUpdating();
    };

    private onSubmit = async () => {
        const enteredDOB = this.state.date || '';
        let oldEnough = false;
        try {
            const dobInMs = Date.parse(enteredDOB);
            // @ts-ignore
            const ageInMs = Date.now() - dobInMs;
            oldEnough = ageInMs > MIN_AGE && dobInMs > MAX_BIRTHDAY;
        } catch(e) {
            // failed to parse.
        }

        if (!oldEnough) {
            this.props.moduleState.setHasError(true);
        } else {
            // tslint:disable-next-line: no-floating-promises
            await this.addDOBToCart();
            this.props.moduleState.setHasError(false);
            this.props.moduleState.onReady();
            this.setState({ submitted: true });
        }
    };

    private onCancel = (): void => {
        this.props.moduleState.setHasError(false);
        this.props.moduleState.onReady();
        this.setState({ submitted: true });
    };

    private onChange = () => {
        const date = this.inputRef.current && this.inputRef.current.value;
        this.setState({date});
    };

    private addDOBToCart = async () => {
        const checkoutState = this.props.data.checkoutState && this.props.data.checkoutState.status === 'SUCCESS' && this.props.data.checkoutState.result;
        if (!checkoutState || !checkoutState.checkoutCart || !checkoutState.checkoutCart.cart || !checkoutState.checkoutCart.cart.ExtensionProperties || !this.state.date) {
            this.props.context.telemetry.error('Failed to add DOB to Cart. Unable to load cart');
            return;
        } else {
            const extensionValue = {
                StringValue: moment(this.state.date).format('MM/DD/YYYY')
            };
            const extensionProperties = checkoutState.checkoutCart.cart.ExtensionProperties as CommerceProperty[] | undefined;
            if(extensionProperties) {
                const property = extensionProperties.find((extension) => extension.Key === 'Birthday');
                if (!property) {
                    extensionProperties.push({
                        Key: 'Birthday',
                        Value: extensionValue
                    });
                } else {
                    property.Value = extensionValue;
                }

                const updatedCart = await updateAsync({ callerContext: this.props.context.actionContext }, checkoutState.checkoutCart.cart);
                this.props.context.actionContext.update(new GetCheckoutCartInput(this.props.context.request.apiSettings), updatedCart);
                // tslint:disable-next-line: no-floating-promises
                await ((await this.props.data.checkoutState).checkoutCart).refreshCart({});
            }
        }
    };
}

export default CheckoutDateOfBirth;
